import { NestingOutput, PartOrientationsItem, UpdateLoadingPlanPayload } from '../../contexts/LoadingPlanContext/LoadingPlanContext';
import { axiosInstance } from './apiAxiosInstance';
import { Part } from './partsService';
import { LoadingPlanStatus } from '../../types/loadingPlan';
import { downloadPdfBlob } from '../../shared/helpers';

export interface LoadingPlanResponse {
  id: number;
  title: string;
  status: LoadingPlanStatus;
  ifcFileUrl: string | null;
  shipDate: string | null;
  trailerLength: number;
  trailerMaxWidth: number;
  trailerMaxWeight: number;
  trailerMaxHeight: number;
  nestingOutput: NestingOutput | null;
  nestingCoverageComplete?: boolean;
  pdfFileStorageId: string | null;
  shareToken: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  includedParts: Part[];
  notIncludedParts: Part[];
  includedPartsCount: string;
}

export interface LoadingPlansListResponse {
  loadingPlans: LoadingPlanResponse[];
  total: number;
  limit: number;
  offset: number;
  averageUtilization: number;
  statusCounts: {
    inProgress: number;
    draft: number;
  };
}

export interface FetchLoadingPlansParams {
  limit?: number;
  offset?: number;
  status?: LoadingPlanStatus;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface LoadingPlanInfoResponse {
  activeJobs: number;
  draftPlans: number;
  avgUtilization: number;
}

export interface NestedPartPayload {
  assigned_loading_layer: number;
  order_number: number;
  part_id: string;
  part_index?: number;
  // Index into the part's precomputed stable orientations (see INPUT_JSON_FORMAT.md in the
  // engine repo). Must be forwarded for a preplaced/locked part carried over from a previous
  // call's output - omitting it makes the engine default to -1, which is only correct for a
  // part it has genuinely never oriented yet.
  orientation_index?: number;
  transform_matrix: number[][];
}

// An obstacle for part-positions: only which part sits where (world matrix) matters to the engine.
export type PartPositionsObstacle = Pick<
  NestedPartPayload,
  'part_id' | 'part_index' | 'orientation_index' | 'transform_matrix'
>;

export interface PartPositionsRequest {
  partIndex: number;
  orientationIndex: number;
  zLevel?: number;
  nestedParts?: PartPositionsObstacle[];
}

export interface PartPositionsZLevel {
  zLevel: number;
  gridCellSize: number;
  baseOrientationTransform: number[][];
  gridPositions: [number, number, number][];
}

export interface PartPositionsResponse {
  zLevels: PartPositionsZLevel[];
}

export interface NestingRequestPayload {
  loadingPlanId: number;
  nested_parts?: NestedPartPayload[];
  // part_index values the engine should consider placing this call, in order.
  nesting_order?: number[];
  // Max number of nesting_order parts to actually place this call. Omitted entirely for a
  // full/unbounded nest (Step 3's initial call, or the Finish Layout completion nest).
  nesting_limit?: number;
}

const loadingPlansService = {
  /**
   * Fetch all loading plans with optional filters
   */
  async fetchLoadingPlans(params?: FetchLoadingPlansParams): Promise<LoadingPlansListResponse> {
    const response = await axiosInstance.get<LoadingPlansListResponse>('/api/v1/loading-plans', {
      params,
    });
    return response.data;
  },

  /**
   * Fetch a single loading plan by ID
   */
  async fetchLoadingPlanById(id: number): Promise<LoadingPlanResponse> {
    const response = await axiosInstance.get<LoadingPlanResponse>(`/api/v1/loading-plans/${id}`);
    return response.data;
  },

  /**   
   * Fetch a shared loading plan by token
   */
  async fetchSharedLoadingPlanByToken(token: string): Promise<LoadingPlanResponse> {
    const response = await axiosInstance.get<LoadingPlanResponse>(`/api/v1/loading-plans/shared/view/${token}`);
    return response.data;
  },

  /**
   * Create a new loading plan
   */
  async createLoadingPlan(data: Partial<LoadingPlanResponse>): Promise<LoadingPlanResponse> {
    const response = await axiosInstance.post<LoadingPlanResponse>('/api/v1/loading-plans', data);
    return response.data;
  },

  /**
   * Update an existing loading plan
   */
  async updateLoadingPlan(id: number, data: Partial<UpdateLoadingPlanPayload>): Promise<LoadingPlanResponse> {
    const response = await axiosInstance.patch<LoadingPlanResponse>(`/api/v1/loading-plans/${id}`, data);
    return response.data;
  },

  /**
   * Candidate orientations of every included part, from the last full nesting run. Not part of the loading plan
   * payload (it can be megabytes); only edit mode needs it.
   */
  async fetchPartsOrientations(id: number): Promise<PartOrientationsItem[]> {
    const response = await axiosInstance.get<PartOrientationsItem[]>(`/api/v1/loading-plans/${id}/parts-orientations`);
    return response.data;
  },

  /**
   * Delete a loading plan
   */
  async deleteLoadingPlan(id: number): Promise<void> {
    await axiosInstance.delete(`/api/v1/loading-plans/${id}`);
  },

  /**
   * Fetch loading plan stats (active jobs, draft plans, avg utilization)
   */
  async fetchInfo(): Promise<LoadingPlanInfoResponse> {
    const response = await axiosInstance.get<LoadingPlanInfoResponse>('/api/v1/loading-plans/info');
    return response.data;
  },

  /**
   * Request nesting calculation for a loading plan
   */
  async requestNesting(data: NestingRequestPayload): Promise<any> {
    const response = await axiosInstance.post<any>('/api/v1/loading-plans/nesting', data);
    return response.data;
  },

  /**
   * Request loading plan shared URL
   */
  async getSharedUrl(id: number): Promise<any> {
    const response = await axiosInstance.post<any>(`/api/v1/loading-plans/${id}/share`, {});
    return response.data;
  },

  /**
   * Set shared loading plan as loaded
   */
  async setLoadedStatus(token: string): Promise<any> {
    const response = await axiosInstance.patch<any>(`/api/v1/loading-plans/shared/${token}/loaded`, {});
    return response.data;
  },

  /**
   * Add selected parts to a loading plan
   */
  async addParts(id: number, partIds: number[]): Promise<{ success: boolean; addedCount: number; totalIncludedParts: number }> {
    const response = await axiosInstance.post<{ success: boolean; addedCount: number; totalIncludedParts: number }>(
      `/api/v1/loading-plans/${id}/parts/add`,
      { partIds }
    );
    return response.data;
  },

  /**
   * Add all parts matching filters to a loading plan
   */
  async addAllParts(
    id: number,
    filters?: {
      type?: string[];
      mark?: string[];
      // Wildcard mark mask ('*', '?'), matched on the server.
      markMask?: string;
      sequenceFrom?: number;
      sequenceTo?: number;
    }
  ): Promise<{ success: boolean; addedCount: number; totalIncludedParts: number }> {
    const response = await axiosInstance.post<{ success: boolean; addedCount: number; totalIncludedParts: number }>(
      `/api/v1/loading-plans/${id}/parts/add-all`,
      { filters: { ...filters } }
    );
    return response.data;
  },

  /**
   * Remove selected parts from a loading plan
   */
  async removeParts(id: number, partIds: number[]): Promise<{ success: boolean; removedCount: number; totalIncludedParts: number }> {
    const response = await axiosInstance.post<{ success: boolean; removedCount: number; totalIncludedParts: number }>(
      `/api/v1/loading-plans/${id}/parts/remove`,
      { partIds }
    );
    return response.data;
  },

  /**
   * Remove all parts from a loading plan
   */
  async removeAllParts(id: number): Promise<{ success: boolean; removedCount: number; totalIncludedParts: number }> {
    const response = await axiosInstance.post<{ success: boolean; removedCount: number; totalIncludedParts: number }>(
      `/api/v1/loading-plans/${id}/parts/remove-all`,
      {}
    );
    return response.data;
  },

  /**
   * Export shared loading plan as PDF
   */
  async exportSharedPdf(token: string): Promise<void> {
    const response = await axiosInstance.get(`/api/v1/loading-plans/shared/export-pdf`, {
      params: { loadingPlanId: token },
      responseType: 'blob',
    });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    downloadPdfBlob(blob, `loading-plan-${token}.pdf`);
  },

  /**
   * Fetch valid placement grid positions for a part and orientation
   */
  async fetchPartPositions(
    id: number,
    data: PartPositionsRequest
  ): Promise<PartPositionsResponse> {
    const response = await axiosInstance.post<PartPositionsResponse>(
      `/api/v1/loading-plans/${id}/part-positions`,
      data
    );
    return response.data;
  },
};

export default loadingPlansService;
